// ─────────────────────────────────────────────────────────────────────
// <SponsoredStrip /> — paid placement banner
//
// Wide horizontal slot (1200×280) below trending rail.
// Marked "Promoted" tag. Dismissible.
// Fed by /promotions/active (public endpoint).
// ─────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../api/client';

const STORAGE_DISMISSED = 'snt:promo_dismissed';

export default function SponsoredStrip({ slot = 'home_below_trending', className = '' }) {
  const { t } = useTranslation();
  const [promo, setPromo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/promotions/active', {
          params: { slot },
        });
        if (cancelled) return;
        if (data?.promotion) {
          // Check if user dismissed this specific promo
          const dismissedSet = readDismissed();
          if (!dismissedSet.includes(data.promotion.id)) {
            setPromo(data.promotion);
            // Track impression (fire and forget)
            api.post(`/promotions/${data.promotion.id}/impression`).catch(() => {});
          }
        }
      } catch {
        // No promo active; render nothing
      }
    })();
    return () => { cancelled = true; };
  }, [slot]);

  const handleClick = () => {
    if (!promo) return;
    api.post(`/promotions/${promo.id}/click`).catch(() => {});
  };

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!promo) return;
    setDismissed(true);
    const dismissedSet = readDismissed();
    if (!dismissedSet.includes(promo.id)) {
      writeDismissed([...dismissedSet, promo.id]);
    }
  };

  if (!promo || dismissed) return null;

  return (
    <section
      className={`px-9 pt-2 pb-12 ${className}`}
      aria-label={t('promo.aria', 'Sponsored content')}
    >
      <Link
        to={promo.cta_to || `/e/${promo.event_slug || promo.event_id}`}
        onClick={handleClick}
        className="block relative no-underline rounded-card overflow-hidden border border-line group"
        style={{
          background: 'var(--snt-bg-1)',
          minHeight: 200,
        }}
      >
        {promo.banner_url ? (
          <img
            src={promo.banner_url}
            alt={promo.title}
            className="w-full h-full object-cover absolute inset-0 transition-transform duration-450 group-hover:scale-[1.02]"
            loading="lazy"
          />
        ) : (
          // Editorial fallback if no banner image — gradient + text
          <div
            className="snt-art-gold w-full h-full absolute inset-0"
            style={{ minHeight: 200 }}
          />
        )}

        {/* Editorial overlay text (only if banner has dark side or no banner) */}
        {!promo.banner_url && (
          <div className="relative px-9 py-10 lg:py-14 max-w-[60%]">
            <div className="snt-eyebrow mb-3" style={{ color: 'var(--snt-accent)' }}>
              {promo.eyebrow || 'Featured'}
            </div>
            <h3
              className="font-serif font-light mb-3"
              style={{ fontSize: 'clamp(24px, 4vw, 42px)', lineHeight: 1.05, color: 'var(--snt-text)' }}
            >
              {promo.title}
            </h3>
            {promo.subtitle && (
              <div className="text-[14px]" style={{ color: 'var(--snt-text-2)' }}>
                {promo.subtitle}
              </div>
            )}
          </div>
        )}

        {/* Promoted label */}
        <span
          className="absolute top-4 right-4 snt-eyebrow z-10"
          style={{
            padding: '4px 10px',
            background: 'rgba(0,0,0,0.6)',
            color: 'rgba(255,255,255,0.85)',
            borderRadius: 3,
            fontSize: 9,
            backdropFilter: 'blur(8px)',
          }}
        >
          {t('promo.label', 'Promoted')}
        </span>

        {/* Dismiss button */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('promo.dismiss', 'Dismiss')}
          className="absolute top-3 left-3 w-7 h-7 rounded-full items-center justify-center hidden group-hover:flex z-10"
          style={{
            background: 'rgba(0,0,0,0.6)',
            color: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <X size={13} />
        </button>
      </Link>
    </section>
  );
}

function readDismissed() {
  try {
    const v = localStorage.getItem(STORAGE_DISMISSED);
    return v ? JSON.parse(v) : [];
  } catch { return []; }
}
function writeDismissed(ids) {
  try { localStorage.setItem(STORAGE_DISMISSED, JSON.stringify(ids)); }
  catch { /* ignore */ }
}
